import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { type BannerAudienceView, type BannerScopeKindView, type BannerScopeView, type BannerView } from '@vello/api-client';
import { Badge, Button, EmptyState, Field, ListTable, Notice, PageHeader, Panel, Select, TextArea, TextInput, type Column, SectionTitle, Text, Row, Measure, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  bannerAuthority,
  describeBannerScope,
  formatMarketInstant,
  marketRegionChoices,
  reachableCountries,
  marketTimeZone,
  zonedLocalToUtc,
} from '../../src/index';
import { usePlaceNames } from '../i18n/scope-name';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

interface Draft {
  readonly text: string;
  readonly audience: BannerAudienceView;
  readonly kind: BannerScopeKindView;
  readonly country: string;
  readonly region: string;
  readonly from: string;
  readonly to: string;
}

const EMPTY: Draft = { text: '', audience: 'RIDER', kind: 'MARKET', country: '', region: '', from: '', to: '' };

function scopeOf(d: Draft, market: string): BannerScopeView {
  switch (d.kind) {
    case 'PLATFORM': return { kind: 'PLATFORM' };
    case 'COUNTRY': return { kind: 'COUNTRY', country: d.country.trim().toUpperCase() };
    case 'REGION': return { kind: 'REGION', country: d.country.trim().toUpperCase(), region: d.region };
    case 'MARKET': return { kind: 'MARKET', marketId: market };
  }
}

function bannerState(b: BannerView, now: number): 'live' | 'scheduled' | 'expired' | 'off' {
  if (!b.active) return 'off';
  if (now >= Date.parse(b.endsAt)) return 'expired';
  return now < Date.parse(b.startsAt) ? 'scheduled' : 'live';
}

export function BannersScreen(): ReactElement {
  const { runtime, markets, grants, scope, regions: stateCatalog } = useAdmin();
  const place = usePlaceNames();
  const { t, lang } = useT();
  const { v, fail, refusal: refusalOf } = useVocab();
  const authority = bannerAuthority(grants);
  // A one-market banner belongs to the header scope's market (owner, 2026-10-12) — no second market selector here.
  const { market, choices } = useScopeMarket();
  const timeZone = marketTimeZone(market, markets);

  const [draft, setDraft] = useState<Draft>({ ...EMPTY, country: (market ?? '').slice(0, 2).toUpperCase() });
  // D580: a country or city scope fixes the banner's country (it follows the top-bar scope); only a platform scope types one.
  const scopeCountry = scope.current.country?.toUpperCase();
  // With no country at the top the banner's country is chosen from the countries the operator reaches (never typed).
  const countries = useMemo(() => reachableCountries(scope.allowed, markets, stateCatalog ?? []), [scope.allowed, markets, stateCatalog]);
  useEffect(() => {
    if (scopeCountry !== undefined) setDraft((d) => (d.country === scopeCountry ? d : { ...d, country: scopeCountry }));
    else if (countries.length > 0) setDraft((d) => (countries.includes(d.country) ? d : { ...d, country: countries[0] as string, region: '' }));
  }, [scopeCountry, countries]);
  // ADR-0124: a state banner names one of the country's ACTIVE states, chosen from the list (never typed); a state scope at the top fixes it.
  const scopeRegion = scope.current.region?.toUpperCase();
  const regionChoices = useMemo(() => marketRegionChoices(stateCatalog ?? [], draft.country), [stateCatalog, draft.country]);
  useEffect(() => {
    setDraft((d) => {
      const wanted = scopeRegion !== undefined && regionChoices.some((r) => r.code === scopeRegion) ? scopeRegion : regionChoices.some((r) => r.code === d.region) ? d.region : '';
      return wanted === d.region ? d : { ...d, region: wanted };
    });
  }, [scopeRegion, regionChoices]);
  const [banners, setBanners] = useState<readonly BannerView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const refuse = useCallback(
    (reasonCode: string | undefined, fallback: string, status?: number): string => {
      const text = refusalOf('refusal.admin.broadcast', { code: fallback, reasonCode, status });
      return text ?? t('lmap.err.generic', { code: fail({ code: fallback, reasonCode }) });
    },
    [t, fail, refusalOf],
  );

  const load = useCallback(async (): Promise<void> => {
    const res = await runtime.banners.list({ includeExpired: false });
    if (res.ok) {
      setBanners(res.value.banners);
      setError(undefined);
    } else setError(refuse(res.error.reasonCode, res.error.code, res.error.status));
  }, [runtime, refuse]);

  useEffect(() => {
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue });

  const update = (patch: Partial<Draft>): void => setDraft((d) => ({ ...d, ...patch }));

  const create = async (): Promise<void> => {
    const tz = timeZone ?? 'UTC';
    const startsAt = draft.from.trim() === '' ? undefined : zonedLocalToUtc(draft.from, tz);
    const endsAt = zonedLocalToUtc(draft.to, tz);
    if (endsAt === undefined || (draft.from.trim() !== '' && startsAt === undefined)) {
      setNotice({ tone: 'danger', text: t('bn.err.window') });
      return;
    }
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.banners.create({ text: draft.text.trim(), audience: draft.audience, scope: scopeOf(draft, market ?? ''), ...(startsAt !== undefined ? { startsAt } : {}), endsAt });
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t('bn.done.created') });
      setDraft((d) => ({ ...d, text: '', from: '', to: '' }));
      await load();
    } else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
  };

  const toggle = async (b: BannerView): Promise<void> => {
    setBusy(true);
    const res = await runtime.banners.update(b.bannerId, { active: !b.active });
    setBusy(false);
    if (res.ok) setNotice({ tone: 'success', text: t('bn.done.updated') });
    else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  /** What the scope points at, in words: the country, the state or the city by name (never a code). */
  const scopeRefName = (sc: BannerScopeView): string => {
    switch (sc.kind) {
      case 'PLATFORM': return '';
      case 'COUNTRY': return place.country(sc.country);
      case 'REGION': return place.region(sc.region);
      case 'MARKET': return place.market(sc.marketId);
    }
  };

  const now = Date.now();
  const columns: readonly Column<BannerView>[] = [
    { key: 'text', header: t('bn.col.text'), width: 'minmax(260px, 2.5fr)', render: (b) => <span>{b.text}</span> },
    { key: 'audience', header: t('bn.col.audience'), width: 'minmax(110px, 0.7fr)', render: (b) => v('broadcast.audience', b.audience) },
    { key: 'scope', header: t('bn.col.scope'), width: 'minmax(190px, 1.3fr)', render: (b) => { const s = describeBannerScope(b.scope); return <span><strong>{v('broadcast.scope_kind', s.kind)}</strong> <Text role="caption" tone="secondary">{scopeRefName(b.scope)}</Text></span>; } },
    { key: 'window', header: t('bn.col.window'), width: 'minmax(220px, 1.5fr)', render: (b) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(b.startsAt, timeZone, lang)} → {formatMarketInstant(b.endsAt, timeZone, lang)}</Text> },
    { key: 'status', header: t('bn.col.status'), width: 'minmax(120px, 0.8fr)', render: (b) => { const s = bannerState(b, now); return <Badge tone={s === 'live' ? 'success' : s === 'scheduled' ? 'warning' : 'neutral'}>{v('banner.state', s)}</Badge>; } },
    { key: 'act', header: '', width: 'minmax(130px, 0.8fr)', render: (b) => (authority.mayManage ? <Button variant="ghost" size="sm" disabled={busy} onClick={() => void toggle(b)}>{t(b.active ? 'bn.action.off' : 'bn.action.on')}</Button> : <Text role="caption" tone="secondary">—</Text>) },
  ];

  return (
    <>
      <PageHeader title={t('bn.title')} subtitle={t('bn.subtitle')} />

      {error !== undefined && <div><Notice tone="danger">{error}</Notice></div>}
      {notice !== undefined && <div><Notice tone={notice.tone}>{notice.text}</Notice></div>}

      {authority.mayManage && (
        <Panel>
          <SectionTitle>{t('bn.section.new')}</SectionTitle>
          <Measure size="lg" gap={4}>
            <Field label={t('bn.field.text')} htmlFor="bn-text">
              <TextArea id="bn-text" value={draft.text} maxLength={280} onChange={(e) => update({ text: e.target.value })} />
            </Field>
            <Row gap={4} align="stretch" wrap>
              <Field label={t('bn.field.audience')} htmlFor="bn-audience">
                <Select id="bn-audience" value={draft.audience} onChange={(e) => update({ audience: e.target.value as BannerAudienceView })}>
                  <option value="RIDER">{v('broadcast.audience', 'RIDER')}</option>
                  <option value="DRIVER">{v('broadcast.audience', 'DRIVER')}</option>
                </Select>
              </Field>
              <Field label={t('bn.field.scope')} htmlFor="bn-scope">
                <Select id="bn-scope" value={draft.kind} onChange={(e) => update({ kind: e.target.value as BannerScopeKindView })}>
                  {(['MARKET', 'REGION', 'COUNTRY', 'PLATFORM'] as const).map((k) => <option key={k} value={k}>{v('broadcast.scope_kind', k)}</option>)}
                </Select>
              </Field>
            </Row>
            {scopeCountry === undefined && (draft.kind === 'COUNTRY' || draft.kind === 'REGION') && (
              <Field label={t('bn.field.country')} htmlFor="bn-country">
                <Select id="bn-country" value={draft.country} onChange={(e) => update({ country: e.target.value, region: '' })}>
                  {countries.map((cc) => <option key={cc} value={cc}>{place.country(cc)}</option>)}
                </Select>
              </Field>
            )}
            {draft.kind === 'REGION' && (
              <Field label={t('bn.field.region')} htmlFor="bn-region" hint={regionChoices.length === 0 ? t('bn.field.region.empty') : scopeRegion !== undefined ? t('bn.field.region.fixed') : undefined}>
                <Select id="bn-region" value={draft.region} disabled={regionChoices.length === 0 || scopeRegion !== undefined} onChange={(e) => update({ region: e.target.value })}>
                  <option value="">{t('bn.field.region.pick')}</option>
                  {regionChoices.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                </Select>
              </Field>
            )}
            {draft.kind === 'MARKET' && market === undefined && <ScopeMarketNotice choices={choices} />}
            <Row gap={4} align="stretch" wrap>
              <Field label={t('bn.field.from')} htmlFor="bn-from" hint={timeZone ?? 'UTC'}>
                <TextInput id="bn-from" className="tnum" type="datetime-local" value={draft.from} onChange={(e) => update({ from: e.target.value })} />
              </Field>
              <Field label={t('bn.field.to')} htmlFor="bn-to" hint={timeZone ?? 'UTC'}>
                <TextInput id="bn-to" className="tnum" type="datetime-local" value={draft.to} onChange={(e) => update({ to: e.target.value })} />
              </Field>
            </Row>
            <div>
              <Button variant="primary" disabled={busy || draft.text.trim() === '' || draft.to.trim() === '' || (draft.kind === 'MARKET' && market === undefined) || (draft.kind === 'REGION' && draft.region === '')} onClick={() => void create()}>{t('bn.action.create')}</Button>
            </div>
          </Measure>
        </Panel>
      )}

      <SectionTitle>{t('bn.section.list')}</SectionTitle>
      <ListTable
        columns={columns}
        rows={banners ?? []}
        rowKey={(b) => b.bannerId}
        loading={banners === undefined && error === undefined}
        loadingLabel={t('ui.loading')}
        error={error !== undefined ? t('bn.loadFailed') : undefined}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('bn.empty')} />}
        minWidth={1000}
      />
    </>
  );
}
